import { toast } from 'react-toastify';
import {
    Category,
    CategoryWithProducts,
    CategoryWithSubCategories,
    CreateCategoryBody,
    CreateSubCategoryBody,
    SubCategory,
} from '@core/models/category.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import CategoryService from 'services/api/category.service';

// -------------------------------------------------------------------------

interface ICategoryState {
    loading: boolean;
    createLoading: boolean;
    updateLoading: boolean;
    deleteLoading: boolean;
    getByIdLoading: boolean;
    error: IErrorModel | undefined;
    categories: Category[];
    categoryWithSub: CategoryWithSubCategories | null;
    subCategories: SubCategory[];
    subCategory: SubCategory | null;
    subCategoryWithCategory: []
}

const initialState: ICategoryState = {
    loading: false,
    createLoading: false,
    updateLoading: false,
    deleteLoading: false,
    getByIdLoading: false,
    error: {} as IErrorModel,
    categories: [],
    categoryWithSub: null,
    subCategories: [],
    subCategory: null,
    subCategoryWithCategory: [],
};

// -------------------------------------------------------------------------

export const getCategoriesThunk = createAsyncThunk<Category[], void, { rejectValue: IErrorModel }>(
    `/category/getCategories`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await CategoryService.getCategories();
            return response.data.Categories;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch categories', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const getCategoriesWithSubThunk = createAsyncThunk<
    CategoryWithSubCategories,
    void,
    { rejectValue: IErrorModel }
>(`/category/getCategoriesWithSub`, async (_, { rejectWithValue }: any) => {
    try {
        const response: any = await CategoryService.getCategoriesWithSub();
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to fetch categories', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const addCategoryThunk = createAsyncThunk<
    ISuccessResponse,
    CreateCategoryBody,
    { rejectValue: IErrorModel }
>(`/category/addCategory`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await CategoryService.addCategory(body);
        toast.success(response.data.message || 'Category created successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to create category', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const getSubCategoriesThunk = createAsyncThunk<SubCategory[], void, { rejectValue: IErrorModel }>(
    `/category/getSubCategories`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await CategoryService.getSubCategories();
            return response.data.subCategory
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch sub categories', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const addSubCategoryThunk = createAsyncThunk<
    ISuccessResponse,
    CreateSubCategoryBody,
    { rejectValue: IErrorModel }
>(`/category/addSubCategory`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await CategoryService.addSubCategory(body);
        toast.success(response.data.message || 'Sub category created successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to create sub category', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const getSubCategoryByIdThunk = createAsyncThunk<SubCategory, number, { rejectValue: IErrorModel }>(
    `/category/getSubCategoryById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await CategoryService.getSubCategoryById(id);
            return response.data;
        }
        catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch sub category', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const getCategoryWithSubByIdThunk = createAsyncThunk<CategoryWithSubCategories, number, { rejectValue: IErrorModel }>(
    `/category/getCategoryWithSubById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await CategoryService.getCategoryWithSubById(id);
            return response.data
        }
        catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch category with sub', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
// -------------------------------------------------------------------------

const categorySlice = createSlice({
    name: `category`,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Get Categories
        builder.addCase(getCategoriesThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getCategoriesThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.categories = action.payload;
        });
        builder.addCase(getCategoriesThunk.rejected, (state, action) => {
            state.loading = false;
            state.categories = [];
            state.error = action.payload;
        });

        // Get Categories With Sub
        builder.addCase(getCategoriesWithSubThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getCategoriesWithSubThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.categoryWithSub = action.payload;
        });
        builder.addCase(getCategoriesWithSubThunk.rejected, (state, action) => {
            state.loading = false;

            state.error = action.payload;
        });

        // Add Category
        builder.addCase(addCategoryThunk.pending, (state) => {
            state.createLoading = true;
        });
        builder.addCase(addCategoryThunk.fulfilled, (state) => {
            state.createLoading = false;
        });
        builder.addCase(addCategoryThunk.rejected, (state, action) => {
            state.createLoading = false;
            state.error = action.payload;
        });

        // Get Sub Categories
        builder.addCase(getSubCategoriesThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getSubCategoriesThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.subCategories = action.payload;
        });
        builder.addCase(getSubCategoriesThunk.rejected, (state, action) => {
            state.loading = false;
            state.subCategories = [];
            state.error = action.payload;
        });

        // Add Sub Category
        builder.addCase(addSubCategoryThunk.pending, (state) => {
            state.createLoading = true;
        });
        builder.addCase(addSubCategoryThunk.fulfilled, (state) => {
            state.createLoading = false;
        });
        builder.addCase(addSubCategoryThunk.rejected, (state, action) => {
            state.createLoading = false;
            state.error = action.payload;
        });

        builder.addCase(getSubCategoryByIdThunk.pending, (state) => {
            state.getByIdLoading = true;
        });
        builder.addCase(getSubCategoryByIdThunk.fulfilled, (state, action) => {
            state.getByIdLoading = false;
            state.subCategory = action.payload;
        });
        builder.addCase(getSubCategoryByIdThunk.rejected, (state, action) => {
            state.getByIdLoading = false;
            state.error = action.payload;
        });

        builder.addCase(getCategoryWithSubByIdThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getCategoryWithSubByIdThunk.fulfilled, (state: any, action) => {
            state.loading = false;
            state.subCategoryWithCategory = action.payload;
        });
        builder.addCase(getCategoryWithSubByIdThunk.rejected, (state: any, action) => {
            state.loading = false;
            state.subCategoryWithCategory = null;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default categorySlice;
export const categoryReducer = categorySlice.reducer;

